import type { SessionSummary } from '@/app/app/research-assistant/types';

export interface SlackThreadPreference {
  sessionId: string;
  pinned: boolean;
  hidden: boolean;
}

export function shouldShowWorkspaceSlackRail(
  workspaceId: string | null,
  state: {
    loading: boolean;
    error: string | null;
    sessions: unknown[];
    hasMore: boolean;
  },
): boolean {
  return Boolean(
    workspaceId && (state.loading || state.error || state.sessions.length || state.hasMore),
  );
}

export function slackPublicDelivery(value: Record<string, unknown> | null | undefined) {
  const active = value?.status === 'streaming';
  return {
    active,
    text: active && typeof value.text === 'string' ? value.text.slice(0, 24000) : '',
    progress:
      active && Array.isArray(value.progress)
        ? value.progress
          .flatMap(item => {
            if (
              !item ||
                typeof item.title !== 'string' ||
                !['in_progress', 'complete', 'error'].includes(item.status)
            )
              return [];
            return [{ title: item.title.slice(0, 200), status: String(item.status) }];
          })
          .slice(-6)
        : [],
  };
}

export function workspaceThreadHref(sessionId: string, workspaceId: string): string {
  return `/app/workspace?${new URLSearchParams({ session: sessionId, session_scope: 'workspace', workspace_id: workspaceId })}`;
}

export function canPreserveWorkspaceThread(
  search: string,
  targetWorkspaceId?: string | null,
): boolean {
  const params = new URLSearchParams(search);
  return Boolean(
    targetWorkspaceId &&
      params.get('session_scope') === 'workspace' &&
      (params.get('session') || params.get('sessionId')) &&
      params.get('workspace_id') === targetWorkspaceId,
  );
}

export function researchUiCacheKey(
  userId: string | null | undefined,
  workspaceId: string | null | undefined,
  projectId?: string | null,
): string | null {
  return userId && workspaceId ? JSON.stringify([userId, workspaceId, projectId || null]) : null;
}

export function visibleSlackThreads(
  sessions: SessionSummary[],
  preferences: SlackThreadPreference[],
  showHidden = false,
): SessionSummary[] {
  const bySession = new Map(preferences.map(preference => [preference.sessionId, preference]));
  return sessions
    .filter(session => showHidden || !bySession.get(session.id)?.hidden)
    .sort(
      (a, b) =>
        Number(Boolean(bySession.get(b.id)?.pinned)) -
          Number(Boolean(bySession.get(a.id)?.pinned)) ||
        b.updatedAt.localeCompare(a.updatedAt) ||
        a.id.localeCompare(b.id),
    );
}
